/**
 * Phase 18: empty entry forms and the bundle of all form states the entry form edits.
 * Numbers stay strings while typing; '' means "not measured" and is never sent.
 */
import type {
  FeedFormData,
  IncidentFormData,
  MarFormData,
  MeasurementsFormData,
  NoteFormData,
  OutputFormData,
  RespiratoryFormData,
  SeizureFormData,
  SupervisoryFormData,
  TherapyFormData,
  AideTasksFormData,
  VitalFormData,
  WoundFormData,
} from '../types';
import type { TemplateNoteFormData } from './noteTemplates';

export const emptyVitals = (): VitalFormData => ({
  temperature: '',
  bloodPressure: '',
  bloodPressureSystolic: '',
  bloodPressureDiastolic: '',
  pulse: '',
  respiratoryRate: '',
  o2Saturation: '',
  painScale: '',
  painScore: '',
  o2FlowLpm: '',
});

export const emptyMar = (administrationTime: string): MarFormData => ({
  medicationId: '',
  medicationName: '',
  doseAmount: '',
  doseUnit: '',
  route: '',
  administrationTime,
  notes: '',
  status: 'GIVEN',
  withholdReason: '',
  indication: '',
  effectiveness: '',
  followUpOf: '',
  site: '',
  reaction: '',
});

export const emptyFeed = (): FeedFormData => ({
  feedType: '',
  formula: '',
  volumeMl: '',
  durationMinutes: '',
  tolerance: '',
  notes: '',
  rateMlPerHour: '',
  residualMl: '',
});

export const emptyNote = (): NoteFormData => ({
  note: '',
  category: 'GENERAL',
  visibility: 'CHART',
  teaching: { subject: '', learner: '', method: '', durationMinutes: '', understanding: '' },
});

export const emptyWound = (): WoundFormData => ({
  woundLocation: '',
  woundDescription: '',
  drainage: '',
  treatmentPerformed: '',
  length: '',
  width: '',
  depth: '',
  units: 'cm',
});

export const emptyIncident = (): IncidentFormData => ({
  incidentType: '',
  severity: '',
  description: '',
  internalOnly: false,
  actionsTaken: '',
  reportedTo: '',
  witnesses: '',
  injuryDetails: '',
});

export const emptyMeasurements = (): MeasurementsFormData => ({
  height: '',
  heightUnit: 'in',
  weight: '',
  weightUnit: 'lb',
  headCircumference: '',
  headCircumferenceUnit: 'in',
});

export const emptyRespiratory = (): RespiratoryFormData => ({
  mode: '',
  ventMode: '',
  rate: '',
  tidalVolumeMl: '',
  pip: '',
  peep: '',
  fio2: '',
  o2FlowLpm: '',
  spo2: '',
  suctionCount: '',
  secretionAmount: '',
  secretionColor: '',
  tolerance: '',
  alarmsChecked: false,
  circuitChecked: false,
  notes: '',
});

export const emptySeizure = (startedTime: string): SeizureFormData => ({
  startedTime,
  durationMinutes: '',
  durationSeconds: '',
  seizureType: '',
  description: '',
  interventions: [],
  rescueMedEntryId: '',
  postIctalState: '',
  notified: '',
  notes: '',
});

export const emptyOutput = (): OutputFormData => ({
  outputType: '',
  volumeMl: '',
  diaperCount: '',
  characteristics: '',
  notes: '',
});

export const emptySupervisory = (): SupervisoryFormData => ({
  supervisedUserId: '',
  supervisedDiscipline: '',
  staffPresent: '',
  pocFollowed: '',
  competenciesObserved: [],
  clientSatisfaction: '',
  issues: '',
  actions: '',
  nextDueDate: '',
});

export const emptyTherapy = (): TherapyFormData => ({
  evaluationCode: '',
  lines: [{ code: '', minutes: '' }],
  interventions: '',
  goalsAddressed: [],
  homeProgram: '',
  patientResponse: '',
  coTreatment: false,
  coTreatmentWith: '',
  telehealth: false,
});

export const emptyAideTasks = (): AideTasksFormData => ({
  tasks: [],
  observations: '',
  reportedToSupervisor: false,
});

/** Every entry form's state, by local entry type */
export interface EntryFormBundle {
  vitals: VitalFormData;
  mar: MarFormData;
  feed: FeedFormData;
  notes: NoteFormData;
  wound: WoundFormData;
  incident: IncidentFormData;
  summary: string;
  measurements: MeasurementsFormData;
  respiratory: RespiratoryFormData;
  seizure: SeizureFormData;
  output: OutputFormData;
  supervisory: SupervisoryFormData;
  therapy: TherapyFormData;
  aidetasks: AideTasksFormData;
  /** Phase 20E: the open note template's fields (one note form is open at a time) */
  templatenote: TemplateNoteFormData;
}
